/**
 * Generate initials from a name
 * @param {string} name - Full name or first name + last name
 * @returns {string} - Initials (e.g., "John Doe" -> "JD")
 */
export const getInitials = (name) => {
  if (!name) return 'U';
  
  const nameParts = name.trim().split(/\s+/);
  
  if (nameParts.length === 1) {
    // Single name - take first 2 letters
    return nameParts[0].substring(0, 2).toUpperCase();
  }
  
  // Multiple names - take first letter of first and last name
  const firstInitial = nameParts[0].charAt(0).toUpperCase();
  const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
  
  return firstInitial + lastInitial;
};

/**
 * Generate initials from first and last name separately
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} - Initials (e.g., "John", "Doe" -> "JD")
 */
export const getInitialsFromNames = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  
  if (first && last) {
    return first + last;
  } else if (first) {
    return first + first; // Use first letter twice if no last name
  } else if (last) {
    return last + last; // Use last letter twice if no first name
  }
  
  return 'U'; // Default to 'U' for User
};

/**
 * Generate a data URL for an avatar with initials
 * @param {string} initials - Initials to display
 * @param {number} size - Size of the avatar in pixels (default: 100)
 * @param {string} bgColor - Background color (default: random based on initials)
 * @param {string} textColor - Text color (default: white)
 * @returns {string} - Data URL of the avatar image
 */
export const generateInitialsAvatar = (initials, size = 100, bgColor = null, textColor = '#FFFFFF') => {
  // Generate a consistent color based on initials if not provided
  if (!bgColor) {
    const colors = [
      '#0e4410', // Dark green
      '#16a34a', // Green
      '#15803d', // Darker green
      '#22c55e', // Light green
      '#059669', // Emerald
      '#047857', // Dark emerald
      '#065f46', // Very dark emerald
      '#10b981', // Bright green
    ];
    
    // Use initials to pick a consistent color
    const hash = initials.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    bgColor = colors[hash % colors.length];
  }
  
  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  
  // Draw background circle
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.fill();
  
  // Draw text
  ctx.fillStyle = textColor;
  ctx.font = `bold ${size * 0.4}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials, size / 2, size / 2);
  
  return canvas.toDataURL('image/png');
};

/**
 * Get avatar URL or generate initials avatar
 * @param {string} avatarUrl - Avatar URL from API
 * @param {string} fullName - Full name for initials fallback
 * @param {string} firstName - First name (optional, for better initials)
 * @param {string} lastName - Last name (optional, for better initials)
 * @param {number} size - Size of avatar (default: 100)
 * @returns {string} - Avatar URL or data URL
 */
export const getAvatarUrl = (avatarUrl, fullName = '', firstName = '', lastName = '', size = 100) => {
  // If avatar URL exists and is valid, return it
  if (avatarUrl) {
    if (avatarUrl.startsWith('http')) {
      return avatarUrl;
    }
    // If it's a relative URL, construct full URL
    const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseUrl}${avatarUrl}`;
  }
  
  // Generate initials-based avatar as fallback
  let initials;
  if (firstName || lastName) {
    initials = getInitialsFromNames(firstName, lastName);
  } else {
    initials = getInitials(fullName);
  }
  
  return generateInitialsAvatar(initials, size);
};

